<template>
  <div class="flex flex-col glass-frosted rounded-8xl w-83 h-67.5 py-4 gap-4">
    <div class="flex items-center justify-between h-6 px-4">
      <span class="text-l-medium text-black-white">{{ $t('dpi.gear.title') }}</span>
      <Button
        v-show="!isDeleting"
        color="glass"
        shape="square"
        size="icon-xs"
        @click="isDeleting = true"
      >
        <Icon icon="Function-Wireframe-Delete" />
      </Button>
      <div v-show="isDeleting" class="cursor-pointer" @click="isDeleting = false">
        <span
          class="text-s-medium text-black-white underline hover:text-text-glass-default transition-colors"
        >
          {{ $t('dpi.gear.cancelSelection') }}
        </span>
      </div>
    </div>
    <ScrollArea class="pl-4 pr-2 mr-2">
      <div class="grid grid-cols-3 gap-2">
        <Button
          color="normal"
          square="corner"
          class="flex items-center justify-center gap-1 px-3 py-2 whitespace-nowrap"
          :disabled="isAddDisabled || isSwitchLoading"
          @click="handleAddGear"
        >
          <Icon icon="Function-Wireframe-Add11" class="w-5.5 h-5.5" />
        </Button>
        <Button
          v-for="gear in dpiGears"
          :key="gear.id"
          color="normal"
          square="corner"
          class="flex items-center justify-center gap-1 py-0! px-3 h-10 whitespace-nowrap"
          :class="{
            'cursor-default': isDeleting,
          }"
          :active="gear.id === currentGearId"
          :disabled="isSwitchLoading"
          @click="() => handleClickGear(gear.id)"
        >
          <Icon
            v-if="isSwitchLoading && gear.id === currentGearId"
            icon="Function-Wireframe-CW"
            class="w-4 h-4 animate-spin"
          />
          <Icon
            v-show="isDeleting"
            icon="Status-Wireframe-Close"
            class="w-4 h-4 cursor-pointer transition-colors hover:text-text-hover"
            :class="{ 'cursor-not-allowed select-none': isDeleteDisabled }"
            @click="() => handleDeleteGear(gear.id)"
          />
          <span v-if="!gear.independentXY">{{ gear.x }}</span>
          <div v-else class="flex flex-col gap-1.5 leading-3">
            <span>X{{ gear.x }}</span>
            <span>Y{{ gear.y }}</span>
          </div>
        </Button>
      </div>
    </ScrollArea>

    <Dialog
      v-model:open="isDeleteDialogOpen"
      :title="$t('dpi.gear.deleteDialog.title')"
      content-class="w-90"
    >
      <div class="py-2 text-m-medium text-text-glass-default">
        {{ $t('dpi.gear.deleteDialog.content') }}
      </div>
      <template #footer>
        <div class="flex gap-2 justify-end">
          <Button color="black" @click="isDeleteDialogOpen = false">
            {{ $t('dpi.gear.cancel') }}
          </Button>
          <Button color="text-b-w" @click="handleConfirmDelete">
            {{ $t('dpi.gear.delete') }}
          </Button>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, toRefs } from 'vue'
  import { v4 } from 'uuid'
  import type { GearType } from '@rapoo/device-sdk'
  import { Button, Icon, ScrollArea, Dialog } from '@rapoo/element'

  const MIN_DPI_GEAR_COUNT = 1
  const MAX_DPI_GEAR_COUNT = 7

  const DEFAULT_DPI = 800

  const props = defineProps<{
    dpiGears: GearType[]
    currentGearId: string
  }>()
  const { dpiGears, currentGearId } = toRefs(props)

  const emits = defineEmits<{
    'gear-change': [gear: GearType]
    'gears-change': [gears: GearType[]]
  }>()

  const isSwitchLoading = ref(false)
  const isDeleting = ref(false)
  const deletingId = ref<string | null>(null)
  const isDeleteDialogOpen = ref(false)

  const isAddDisabled = computed(
    () => isDeleting.value || (dpiGears.value && dpiGears.value.length >= MAX_DPI_GEAR_COUNT),
  )
  const isDeleteDisabled = computed(() => dpiGears.value.length === MIN_DPI_GEAR_COUNT)

  const handleClickGear = (id: string) => {
    if (isDeleting.value) return

    const index = dpiGears.value.findIndex((item) => item.id === id)
    if (index === -1) return

    isSwitchLoading.value = true
    emits('gear-change', dpiGears.value[index])
    // 实际生效会慢一点
    setTimeout(() => {
      isSwitchLoading.value = false
    }, 800)
  }

  const handleAddGear = () => {
    const gear: GearType = {
      id: v4(),
      x: DEFAULT_DPI,
      y: DEFAULT_DPI,
      independentXY: false,
      enable: true,
    }

    const gears = [...dpiGears.value, gear]

    emits('gears-change', gears)
  }

  const handleDeleteGear = (id: string) => {
    if (isDeleteDisabled.value) return

    isDeleteDialogOpen.value = true
    deletingId.value = id
  }

  // 如果删除的是当前档位，默认选中第一个
  const handleConfirmDelete = () => {
    const gears: GearType[] = dpiGears.value.filter((item) => item.id !== deletingId.value)
    emits('gears-change', gears)

    const gear = dpiGears.value.find((item) => item.id === currentGearId.value)
    if (currentGearId.value === deletingId.value) {
      emits('gear-change', gears[0])
    } else {
      if (gear) emits('gear-change', gear)
    }

    isDeleteDialogOpen.value = false
  }
</script>

<style scoped></style>
